import React from 'react';

import { UserByRequestResponse } from '../../../../typings/api/skymap/rest/v0/user';
import { TokenService } from '../../services/authentication/token.service';
import {
  renewZitadelAccessToken,
  startZitadelTokenRenewal,
} from '../../services/authentication/zitadel-token-renewal';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { resolveApiUrl } from '../../utils/core/endpoints';
import { getSharedFolderId, getSharedViewId, isPublicRegistrationRoute } from '../routing/utils';
import { createUnauthorizedHandler } from './unauthorized-handler';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export type AuthUser = UserByRequestResponse['data'];

export interface AuthContextValue {
  status: AuthStatus;
  token?: string;
  user?: AuthUser;
  login: (token: string) => Promise<void>;
  loginInMemory: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  intendedRoute?: string;
  setIntendedRoute: (path?: string) => void;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const tokenService = new TokenService();
let axiosInitialized = false;

type Props = {
  children: React.ReactNode;
};

export const AuthStateProvider = ({ children }: Props) => {
  const [status, setStatus] = React.useState<AuthStatus>('idle');
  const [token, setToken] = React.useState<string | undefined>();
  const [user, setUser] = React.useState<AuthUser | undefined>();
  const [intendedRoute, setIntendedRoute] = React.useState<string | undefined>();

  // Read by the 401 handler, which is built once and so cannot close over the current status.
  const statusRef = React.useRef(status);
  statusRef.current = status;

  const logout = React.useCallback(() => {
    setIntendedRoute(undefined);
    tokenService.remove();
    setToken(undefined);
    setUser(undefined);
    setStatus('unauthenticated');
    return Promise.resolve();
  }, []);

  React.useEffect(() => {
    if (axiosInitialized) {
      return;
    }

    const apiUrl = resolveApiUrl();
    SkyMapAxiosServiceFactory.instance.setup(apiUrl, tokenService);

    const isEmbeddedInIframe = window.self !== window.top;

    // Renew the session where it can be renewed, and otherwise sign out and redirect to signin.
    // See `createUnauthorizedHandler` for the order it decides in, and the routes it leaves alone.
    SkyMapAxiosServiceFactory.instance.setUnauthorizedHandler(
      createUnauthorizedHandler({
        isAuthenticated: () => statusRef.current === 'authenticated',
        isEmbeddedInIframe,
        logout,
        renewAccessToken: renewZitadelAccessToken,
        // Straight to the token service rather than through `login`: a renewal is not a sign-in, so
        // the user must not be reloaded, and a request waiting on the retry needs the token now.
        onAccessTokenRenewed: (renewedToken) => {
          tokenService.set(renewedToken);
          setToken(renewedToken);
        },
      }),
    );

    // Register handler for 403 errors - redirect to project list (user is authenticated but lacks permission)
    SkyMapAxiosServiceFactory.instance.setForbiddenHandler(() => {
      // Don't redirect when running inside an iframe (e.g. session link).
      // Navigating to /projects inside the iframe would expose the portal
      // and allow the user to browse with the access-token permissions.
      if (isEmbeddedInIframe) {
        return;
      }

      // Redirect to project list page
      window.location.href = '/projects';
    });

    axiosInitialized = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - axiosInitialized is module-scoped

  React.useEffect(() => {
    // Arms the renewal that happens ahead of expiry, and takes delivery of what it produces. Runs
    // for every session, signed in or not: a session that has nothing to renew arms nothing, and
    // the listener has to already be in place when the sign-in that follows a callback lands.
    //
    // The token is written straight to the token service rather than through `login`, because a
    // renewal is not a sign-in: the user is unchanged and must not be reloaded, and requests in
    // flight need the new token now.
    return startZitadelTokenRenewal({
      onAccessTokenRenewed: (renewedToken) => {
        tokenService.set(renewedToken);
        setToken(renewedToken);
      },
      // Not a sign-out. The same failure covers a refresh token that has expired and a momentary
      // network problem, and only a real request can tell the difference — the 401 handler above
      // ends the session if there is one to end.
      onRenewalFailed: (error) => {
        console.error('The session could not be renewed ahead of expiry.', error);
      },
    });
  }, []);

  const loadUser = React.useCallback(async () => {
    try {
      const response = await SkyMapAxiosServiceFactory.instance
        .createUserServiceV0()
        .fetchRequestingUser({});
      setUser(response.data);
      setStatus('authenticated');
    } catch {
      setStatus('error');
      setUser(undefined);
    }
  }, []);

  const login = React.useCallback(
    async (newToken: string) => {
      tokenService.set(newToken);
      setToken(newToken);
      setStatus('loading');
      await loadUser();
    },
    [loadUser],
  );

  // Used by the iframe access-token flow — keeps the authentication token in
  // module scope only so it never lands in localStorage.
  const loginInMemory = React.useCallback(
    async (newToken: string) => {
      tokenService.setInMemory(newToken);
      setToken(newToken);
      setStatus('loading');
      await loadUser();
    },
    [loadUser],
  );

  const refreshUser = React.useCallback(async () => {
    setStatus('loading');
    await loadUser();
  }, [loadUser]);

  React.useEffect(() => {
    // Skip authentication check for public routes (shared views, shared folders, registration pages)
    if (getSharedViewId() || getSharedFolderId() || isPublicRegistrationRoute()) {
      setStatus('unauthenticated');
      return;
    }

    const existingToken = tokenService.get();
    if (existingToken) {
      setToken(existingToken);
      setStatus('loading');
      void loadUser();
    } else {
      setStatus('unauthenticated');
    }
  }, [loadUser]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      status,
      token,
      user,
      login,
      loginInMemory,
      logout,
      refreshUser,
      intendedRoute,
      setIntendedRoute,
    }),
    [status, token, user, login, loginInMemory, logout, refreshUser, intendedRoute],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthState = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthState must be used within an AuthStateProvider');
  }
  return context;
};

export const useOptionalAuthState = () => React.useContext(AuthContext);
